import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, Lock, Globe, UserCircle2, Newspaper, MapPinned, Trash2, Clock, EyeOff, Pencil } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ExpandableText } from "@/components/feed/ExpandableText";
import { RelativeTime } from "@/components/feed/RelativeTime";
import { ManageDestinationsDialog } from "@/components/post/ManageDestinationsDialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

const STATUS = { published: null, pending_approval: ["A aguardar aprovação", Clock, "bg-amber-100 text-amber-800"], hidden: ["Ocultada pela moderação", EyeOff, "bg-zinc-200 text-zinc-700"], deleted: ["Retirada", Trash2, "bg-zinc-100 text-zinc-500"], missing: ["Indisponível", EyeOff, "bg-zinc-100 text-zinc-500"] };

function Chip({ icon: Icon, children, testid, tone = "bg-zinc-100 text-zinc-700" }) {
  return <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full ${tone}`} data-testid={testid}><Icon size={11} /> {children}</span>;
}

function DestinationChips({ p }) {
  const d = p.destinations;
  return (
    <div className="flex flex-wrap gap-1.5 mt-2" data-testid={`my-post-destinations-${p.id}`}>
      {d.profile && <Chip icon={UserCircle2} testid={`my-post-dest-profile-${p.id}`}>Meu perfil</Chip>}
      {d.feed && <Chip icon={Newspaper} testid={`my-post-dest-feed-${p.id}`}>Feed geral</Chip>}
      {d.communities.map((c) => {
        const st = STATUS[c.status];
        return (
          <span key={c.id} className="inline-flex items-center gap-1">
            <Chip icon={c.privacy === "private" ? Lock : Globe} testid={`my-post-dest-comm-${p.id}-${c.id}`} tone={st ? "bg-zinc-100 text-zinc-500 line-through" : "bg-zinc-100 text-zinc-700"}>{c.name}</Chip>
            {st && <Chip icon={st[1]} tone={st[2]} testid={`my-post-dest-status-${p.id}-${c.id}`}>{st[0]}</Chip>}
          </span>
        );
      })}
      {d.legacy && <span className="text-[11px] text-zinc-400">(publicação anterior: perfil + feed)</span>}
    </div>
  );
}

function EditBox({ p, onSaved, onCancel }) {
  const [text, setText] = useState(p.text);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/profile/posts/${p.id}`, { text });
      const re = data.sync?.reapproval || [];
      toast.success(re.length ? `Guardado · ${re.join(", ")}: volta a aguardar aprovação` : "Guardado em todos os destinos");
      onSaved(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Erro ao guardar"); }
    finally { setBusy(false); }
  };
  return (
    <div className="mt-2" data-testid={`my-post-edit-${p.id}`}>
      <textarea value={text} onChange={(e) => setText(e.target.value)} maxLength={4000} rows={4} data-testid={`my-post-edit-text-${p.id}`} className="w-full text-sm border border-zinc-300 rounded-xl p-2 outline-none focus:border-black" />
      <p className="text-[11px] text-zinc-500 mt-1">A edição atualiza a publicação em todos os destinos ativos. Em comunidades com aprovação obrigatória, volta a ficar pendente.</p>
      <div className="flex justify-end gap-2 mt-2">
        <button type="button" onClick={onCancel} disabled={busy} data-testid={`my-post-edit-cancel-${p.id}`} className="px-3 py-1.5 rounded-xl border border-zinc-300 text-xs font-semibold">Cancelar</button>
        <button type="button" onClick={save} disabled={busy || (!text.trim() && !p.images?.length && !p.video)} data-testid={`my-post-edit-save-${p.id}`} className="px-3 py-1.5 rounded-xl bg-black text-white text-xs font-semibold disabled:opacity-40">{busy ? "A guardar…" : "Guardar"}</button>
      </div>
    </div>
  );
}

export default function MyPosts() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [manage, setManage] = useState(null);
  const [del, setDel] = useState(null);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const focus = params.get("post");

  const load = async (reset = true) => {
    setLoading(true);
    try {
      const { data } = await api.get("/me/posts", { params: reset ? {} : { cursor } });
      setItems(reset ? data.items : (prev) => [...prev, ...data.items]); setCursor(data.next_cursor);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Erro"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(true); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (focus && items.length) { const p = items.find((x) => x.id === focus); if (p) { setManage(p); setParams({}, { replace: true }); } } }, [focus, items]); // eslint-disable-line react-hooks/exhaustive-deps

  const removeAll = async () => {
    setBusy(true);
    try { await api.delete(`/profile/posts/${del.id}`); toast.success("Publicação apagada de todos os destinos"); setItems((prev) => prev.filter((p) => p.id !== del.id)); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Erro"); }
    finally { setBusy(false); setDel(null); }
  };

  return (
    <div className="page max-w-2xl mx-auto px-4" data-testid="my-posts-page">
      <div className="flex items-center gap-2 py-3">
        <Link to="/perfil" className="text-xs text-zinc-500 inline-flex items-center gap-1" data-testid="my-posts-back"><ChevronLeft size={14} /> Perfil</Link>
      </div>
      <h1 className="font-display text-2xl font-extrabold">As minhas publicações</h1>
      <p className="text-xs text-zinc-500 mt-1 mb-4">Área privada: só tu vês esta lista. Aqui geres onde cada publicação aparece. Editar o texto atualiza todos os destinos ativos.</p>
      {!loading && items.length === 0 && <p className="text-sm text-zinc-500 italic text-center py-10" data-testid="my-posts-empty">Ainda não tens publicações.</p>}
      <div className="space-y-3">
        {items.map((p) => (
          <article key={p.id} className="bg-white rounded-2xl border border-zinc-200 p-3" data-testid={`my-post-${p.id}`} data-status={p.status}>
            <div className="flex items-center justify-between gap-2">
              <RelativeTime iso={p.created_at} edited={!!p.edited_at} testid={`my-post-time-${p.id}`} />
              {p.status === "hidden" && <Chip icon={EyeOff} tone="bg-zinc-200 text-zinc-700" testid={`my-post-hidden-${p.id}`}>Oculta no perfil/feed</Chip>}
            </div>
            {editing === p.id ? <EditBox p={p} onCancel={() => setEditing(null)} onSaved={(d) => { setItems((prev) => prev.map((x) => (x.id === p.id ? { ...x, text: d.text, edited_at: d.edited_at } : x))); setEditing(null); }} />
              : <ExpandableText text={p.text} testid={`my-post-text-${p.id}`} className="mt-1" />}
            {p.images?.length > 0 && <p className="text-[11px] text-zinc-500 mt-1">{p.images.length} imagem(ns){p.video ? " · vídeo" : ""}</p>}
            {!p.images?.length && p.video && <p className="text-[11px] text-zinc-500 mt-1">vídeo</p>}
            <DestinationChips p={p} />
            <div className="flex items-center gap-1.5 mt-3 text-xs flex-wrap">
              <button type="button" onClick={() => setManage(p)} data-testid={`my-post-manage-${p.id}`} className="inline-flex items-center gap-1 min-h-[36px] px-2.5 rounded-xl border border-zinc-300 font-semibold"><MapPinned size={13} /> Destinos</button>
              <button type="button" onClick={() => setEditing(editing === p.id ? null : p.id)} data-testid={`my-post-edit-btn-${p.id}`} className="inline-flex items-center gap-1 min-h-[36px] px-2.5 rounded-xl border border-zinc-300 font-semibold"><Pencil size={13} /> Editar</button>
              <Link to={p.destinations.profile ? `/membro/${user.id}?post=${p.id}` : (p.destinations.feed ? `/publicacao/${p.id}` : (p.destinations.communities[0] ? `/comunidade/${p.destinations.communities[0].id}?post=${p.destinations.communities[0].linked_id || ""}` : "#"))}
                    data-testid={`my-post-open-${p.id}`} className="min-h-[36px] px-2.5 inline-flex items-center rounded-xl border border-zinc-200 text-zinc-600">Abrir</Link>
              <button type="button" onClick={() => setDel(p)} data-testid={`my-post-delete-all-${p.id}`} title="Apagar de todos os destinos" className="ml-auto inline-flex items-center gap-1 min-h-[36px] px-2.5 rounded-xl text-[#c8102e] font-semibold"><Trash2 size={13} /> Apagar<span className="hidden sm:inline"> de todos os destinos</span></button>
            </div>
          </article>
        ))}
      </div>
      {cursor && <button onClick={() => load(false)} data-testid="my-posts-more" className="w-full mt-3 py-2 text-sm text-zinc-600 border border-zinc-200 rounded-xl">Ver mais</button>}

      <ManageDestinationsDialog post={manage} open={!!manage} onOpenChange={(o) => !o && setManage(null)} onSaved={(data) => setItems((prev) => prev.map((p) => (p.id === data.id ? { ...p, destinations: data.destinations } : p)))} />

      <AlertDialog open={!!del} onOpenChange={(o) => !o && !busy && setDel(null)}>
        <AlertDialogContent className="rounded-2xl max-w-sm" data-testid="delete-all-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Apagar de todos os destinos?</AlertDialogTitle>
            <AlertDialogDescription>A publicação, com os comentários e reações de cada destino, deixa de estar visível no perfil, no feed e em todas as comunidades. Esta ação é definitiva.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy} data-testid="delete-all-cancel" className="rounded-xl">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); removeAll(); }} disabled={busy} data-testid="delete-all-confirm" className="rounded-xl bg-[#c8102e] hover:bg-[#a50c24]">{busy ? "A apagar…" : "Apagar de todos os destinos"}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
